import React, { useState, useEffect } from 'react';
import { X, Share, Users, Mail, Check, AlertCircle, UserCheck } from 'lucide-react';
import { ContactCollection } from '../types/Collections';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import NotificationToast from './NotificationToast';

import {
    shareCollectionCore,
    getCollectionSharesCore,
    getVaultMembersCore,
    updateShareStatusCore
} from '@gv/core';

import { useNotifications } from '../hooks/useNotifications';


interface CollectionShare {
    id: string;
    collection_id: string;
    shared_by_user_id: string;
    shared_to_email: string;
    status: 'pending' | 'accepted' | 'declined';
    created_at: string;
    accepted_at?: string;
}

export default function ShareCollectionModal({ collection, user, onClose, onShared }: ShareCollectionModalProps) {
    const [vaultMembers, setVaultMembers] = useState<VaultMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
    const [sharing, setSharing] = useState(false);
    const [existingShares, setExistingShares] = useState<CollectionShare[]>([]);
    const { notification, showNotification, hideNotification } = useNotifications();

    const handleCancelInvite = React.useCallback(async (shareId: string) => {
        try {
            const { error } = await supabase
                .from('collection_shares')
                .delete()
                .eq('id', shareId);

            if (error) {
                showNotification('Failed to cancel invitation', 'error');
                return;
            }

            // Update local state to remove the cancelled share
            setExistingShares(prev => prev.filter(share => share.id !== shareId));
            showNotification('Invitation cancelled successfully', 'success');
        } catch (error) {
            console.error('Error cancelling invitation:', error);
            showNotification('Failed to cancel invitation', 'error');
        }
    }, [showNotification]);

    const handleRespondToInvite = React.useCallback(async (shareId: string, status: 'accepted' | 'declined') => {
        try {
            setLoading(true);
            const result = await updateShareStatusCore(supabase, shareId, status);
            if (!result.success) {
                showNotification(`Failed to ${status === 'accepted' ? 'accept' : 'decline'} invitation`, 'error');
                return;
            }

            setExistingShares(prev => prev.map(s => s.id === shareId ? { ...s, status, accepted_at: status === 'accepted' ? new Date().toISOString() : s.accepted_at } : s));
            showNotification(`Invitation ${status === 'accepted' ? 'accepted' : 'declined'}`, 'success');
            if (status === 'accepted' && onShared) {
                try {
                    onShared();
                } catch (e) {
                    // ignore
                }
            }
        } catch (error) {
            console.error('Error responding to invite:', error);
            showNotification('Failed to process invitation', 'error');
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    useEffect(() => {
        fetchVaultMembers();
        fetchExistingShares();
    }, []);

    const fetchVaultMembers = async () => {
        try {
            setLoading(true);

            const result = await getVaultMembersCore(supabase);

            if (!result.success) {
                showNotification(result.error || 'Failed to fetch vault members', 'error');
                return;
            }

            setVaultMembers(result.data || []);
        } catch (error) {
            console.error('Error fetching vault members:', error);
            showNotification('Failed to fetch vault members', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchExistingShares = async () => {
        try {
            const result = await getCollectionSharesCore(supabase, collection.id);

            if (result.success) {
                setExistingShares(result.data || []);
            } else {
                console.error('Error fetching existing shares:', result.error);
            }
        } catch (error) {
            console.error('Error fetching existing shares:', error);
        }
    };

    const handleEmailToggle = (email: string) => {
        setSelectedEmails(prev =>
            prev.includes(email)
                ? prev.filter(e => e !== email)
                : [...prev, email]
        );
    };

    const handleShare = async () => {
        if (selectedEmails.length === 0) {
            showNotification('Please select at least one person to share with', 'error');
            return;
        }

        setSharing(true);
        try {
            const result = await shareCollectionCore(supabase, collection.id, selectedEmails);

            if (!result.success) {
                showNotification(result.error || 'Failed to share collection', 'error');
                return;
            }

            // TODO: Send notification emails to the shared users
            // This would typically involve calling an edge function to send emails

            showNotification(`Collection shared with ${selectedEmails.length} member${selectedEmails.length > 1 ? 's' : ''}`, 'success');
            onShared();
        } catch (error) {
            console.error('Error sharing collection:', error);
            showNotification('Failed to share collection', 'error');
        } finally {
            setSharing(false);
        }
    };

    const getShareStatus = (email: string) => {
        return existingShares.find(share => share.shared_to_email === email);
    };

    const filteredMembers = vaultMembers.filter(member =>
        !existingShares.some(share =>
            share.shared_to_email === member.email &&
            ['pending', 'accepted'].includes(share.status)
        )
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
            <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-600/20 rounded-lg">
                            <Share className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-white">Share Collection</h2>
                            <p className="text-sm text-gray-400">{collection.name}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <span className="ml-3 text-gray-400">Loading vault members...</span>
                        </div>
                    ) : (
                        <>
                            <div className="mb-6">
                                <p className="text-gray-300 text-sm mb-2">
                                    Share this collection with other members of your vault. When they accept,
                                    you'll both be able to add and remove contacts from the shared collection.
                                </p>
                                <div className="flex items-center gap-2 text-xs text-blue-400 bg-blue-900/20 border border-blue-700/30 rounded-lg p-3">
                                    <AlertCircle className="w-4 h-4" />
                                    <span>Collection sharing is available for Family and Business plan members only</span>
                                </div>
                            </div>

                            {/* Existing shares */}
                            {existingShares.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm font-medium text-gray-300 mb-3">Already Shared With</h3>
                                    <div className="space-y-2">
                                        {existingShares.map(share => (
                                            <div key={share.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                                                        <Mail className="w-4 h-4 text-gray-400" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-white">{share.shared_to_email}</p>
                                                        <p className="text-xs text-gray-400">
                                                            Shared {new Date(share.created_at).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                </div>
                                                        <div className="flex items-center gap-2">
                                                            {share.status === 'pending' && (
                                                                <>
                                                                    <span className="text-xs px-2 py-1 bg-yellow-900/30 text-yellow-400 border border-yellow-700/30 rounded mr-2">
                                                                        Pending
                                                                    </span>
                                                                    {((user && (share.shared_to_user_id === (user as any).id)) || (user && (share.shared_to_email || '').toLowerCase() === ((user as any).email || '').toLowerCase())) ? (
                                                                        <>
                                                                            <button
                                                                                onClick={() => handleRespondToInvite(share.id, 'accepted')}
                                                                                className="text-xs px-2 py-1 bg-blue-600 text-white border border-blue-700/30 rounded hover:bg-blue-700 transition-colors mr-2"
                                                                            >
                                                                                Accept
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleRespondToInvite(share.id, 'declined')}
                                                                                className="text-xs px-2 py-1 bg-gray-800 text-red-400 border border-red-700/30 rounded hover:bg-gray-700 transition-colors"
                                                                            >
                                                                                Decline
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => handleCancelInvite(share.id)}
                                                                            className="text-xs px-2 py-1 bg-red-900/30 text-red-400 border border-red-700/30 rounded hover:bg-red-900/50 hover:text-white transition-colors"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                    {share.status === 'accepted' && (
                                                        <span className="text-xs px-2 py-1 bg-green-900/30 text-green-400 border border-green-700/30 rounded">
                                                            Accepted
                                                        </span>
                                                    )}
                                                    {share.status === 'declined' && (
                                                        <span className="text-xs px-2 py-1 bg-red-900/30 text-red-400 border border-red-700/30 rounded">
                                                            Declined
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Available members to share with */}
                            {filteredMembers.length > 0 ? (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-300 mb-3">Share With</h3>
                                    <div className="space-y-2">
                                        {filteredMembers.map(member => (
                                            <div
                                                key={member.id}
                                                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${selectedEmails.includes(member.email)
                                                        ? 'bg-blue-900/30 border-blue-600'
                                                        : 'bg-gray-800 border-gray-700 hover:border-gray-600'
                                                    }`}
                                                onClick={() => handleEmailToggle(member.email)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                                                        {member.profile_image_url ? (
                                                            <img
                                                                src={member.profile_image_url}
                                                                alt={`${member.first_name} ${member.last_name}`}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <span className="text-sm font-medium text-gray-300">
                                                                {((member.first_name?.[0] || '') + (member.last_name?.[0] || '')).toUpperCase() || member.email[0].toUpperCase()}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-white">
                                                            {member.first_name && member.last_name
                                                                ? `${member.first_name} ${member.last_name}`
                                                                : member.email
                                                            }
                                                        </p>
                                                        <p className="text-xs text-gray-400">{member.email}</p>
                                                        <p className="text-xs text-gray-500 capitalize">{member.plan_type} Plan</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    {selectedEmails.includes(member.email) && (
                                                        <Check className="w-5 h-5 text-blue-400" />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                    <p className="text-gray-400">
                                        {vaultMembers.length === 0
                                            ? 'No other vault members found. Make sure you have a Family or Business plan.'
                                            : 'All available members already have access to this collection.'
                                        }
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-700">
                    <p className="text-xs text-gray-400">
                        {selectedEmails.length > 0 && `${selectedEmails.length} member${selectedEmails.length > 1 ? 's' : ''} selected`}
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleShare}
                            disabled={selectedEmails.length === 0 || sharing}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                        >
                            {sharing ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Sharing...
                                </>
                            ) : (
                                <>
                                    <Share className="w-4 h-4" />
                                    Share Collection
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <NotificationToast
                notification={notification}
                onClose={hideNotification}
            />
        </div>
    );
}